import { Table, type TablePaginationConfig } from "antd";
import { type SorterResult } from "antd/lib/table/interface";
import { type FormikErrors, type FormikTouched } from "formik/dist/types";
import update from "immutability-helper";
import type { ExpandableConfig } from "rc-table/lib/interface";
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { useVT } from "virtualizedtableforantd4";

import { cn } from "@btnet/utilities-core";

import { GlobalDnDContext, moveDragItemToHoverPosition } from "utilities/dragAndDrop/dragAndDrop";
import { handleEnterKeyPressed } from "utilities/form/form";
import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTForm } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconPlusCircleFilled } from "commonComponents/btWrappers/BTIcon";
import {
    BTIconCaretDownOutlined,
    BTIconCaretRightOutlined,
} from "commonComponents/btWrappers/BTIcon";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTTable } from "commonComponents/btWrappers/BTTable/BTTable";
import { type IBTColumnType } from "commonComponents/btWrappers/BTTable/BTTable.types";
import { DragAndDropHandle } from "commonComponents/utilities/DragAndDrop/DragAndDrop";
import { BTGridFooterCell } from "commonComponents/utilities/Grid/common/BTGridFooterCell/BTGridFooterCell";
import { CheckedActions } from "commonComponents/utilities/Grid/common/CheckedActions/CheckedActions";
import { HighlightUpdates } from "commonComponents/utilities/HighlightUpdates/HighlightUpdates";
import "commonComponents/utilities/LineItemContainer/LineItemContainer.less";
import { hasMoreThanOneOrderableFormatItems } from "commonComponents/utilities/LineItemContainer/lineItemContainer.utils";
import LineItemDraggableRow from "commonComponents/utilities/LineItemContainer/LineItemDraggableRow";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import {
    type IGridLineItemActionRow,
    type ILineItemGroup,
    type ILineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IAntGridRowProps,
    type IDragProps,
    type IGridLineItemPresentationalProps,
    type LineItemColumns,
    RowType,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import {
    GroupingType,
    isLineItemActionRow,
    isLineItemExpandRow,
    isLineItemGroupRow,
    isLineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { UnassignedCostGroupId } from "entity/estimate/common/estimate.common.types";
import { type IJumpToQuickFindItem } from "entity/estimate/common/LineItemQuickFind/LineItemQuickFind.types";

type LineItem = ILineItemRow | ILineItemGroup;
type SelectedStates = Record<number, boolean | undefined>;

const renderSelectionCell = (
    value: boolean,
    record: ILineItemGroup | ILineItemRow,
    index: number,
    originNode: React.ReactNode,
) => {
    if (
        isLineItemActionRow(record) ||
        isLineItemExpandRow(record) ||
        (isLineItemGroupRow(record) && record.items.length === 0)
    ) {
        return null;
    }
    return originNode;
};

const getRowType = (row: LineItem) => {
    if (isLineItemActionRow(row)) {
        return RowType.Action;
    } else if (isLineItemExpandRow(row)) {
        return RowType.Expand;
    } else if (isLineItemGroupRow(row)) {
        return RowType.Group;
    } else {
        return RowType.LineItem;
    }
};

/**
 * Determines the number of columns the category title / description cell should span based on the given conditions:
 * - If the data is not grouped, always returns 1.
 * - If the category client price is selected and there are only 2 columns, the cell will span 1 column.
 * - If the category client price is not selected and only 1 column is being displayed, the cell will span 1 column.
 * - Otherwise, the cell will span 2 columns to improve readability.
 */
const getColumnSpan = (
    hasOwnerPriceCell: boolean,
    totalDataColumnsCount: number,
    isGroupedData: boolean,
    isTitleColumn: boolean,
    colIndex: number,
) => {
    if (!isGroupedData) return 1; // If not grouped data, always return 1

    const columnThreshhold = hasOwnerPriceCell ? 2 : 1;
    const columnCountForCategory = totalDataColumnsCount <= columnThreshhold ? 1 : 2;

    if (isTitleColumn) {
        return columnCountForCategory;
    } else if (colIndex === 1) {
        // we also must set the next column span to 0 or 1 to prevent styling issues
        return 2 - columnCountForCategory;
    }

    return 1;
};

interface IRefObject {
    scrollTo: (y: number) => void;
    scrollToIndex: (idx: number) => void;
}

const scrollToQuickFindItem = (
    lineItemToJumpTo: IJumpToQuickFindItem,
    quickFindItemIndex: number,
    vtRef: MutableRefObject<IRefObject>,
    tableBody: Element,
    prevScroll: number,
    numRescrolls: number = 0,
) => {
    const currentScroll = tableBody.scrollHeight;

    const jumpIdClass = `Jump${lineItemToJumpTo.id}`;
    const quickFindLineItem = document.querySelector(`.ant-table-row.${jumpIdClass}`);
    if (quickFindLineItem && numRescrolls < 10) {
        // Scroll to item
        quickFindLineItem?.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "start",
        });

        if (currentScroll - prevScroll !== 0) {
            // Rescroll until the item is properly aligned in the view
            const timeout = setTimeout(() => {
                requestAnimationFrame(() =>
                    scrollToQuickFindItem(
                        lineItemToJumpTo,
                        quickFindItemIndex,
                        vtRef,
                        tableBody,
                        currentScroll,
                        numRescrolls + 1,
                    ),
                );
                clearTimeout(timeout);
            }, 500);
        }
    } else if (numRescrolls === 0) {
        // Jump to virtual location of item
        vtRef?.current.scrollToIndex(quickFindItemIndex);

        requestAnimationFrame(() =>
            scrollToQuickFindItem(
                lineItemToJumpTo,
                quickFindItemIndex,
                vtRef,
                tableBody,
                currentScroll,
                1,
            ),
        );
    }
};

export const LineItemContainer: React.FunctionComponent<IGridLineItemPresentationalProps> = ({
    containerName,
    additionalClasses,
    data,
    columnsList,
    onRenderGroupRow,
    scrollable = true,
    setFieldValue,
    setLineItemsTouched,
    lineItemErrors,
    touchedFields,
    lineItemToJumpTo,
    isLineItemDirty,
    onLineItemEditFinished,
    onGroupEditFinished,
    dataField = "lineItems",
    indexOffset = 0,
    onValidateLineItem,
    isEditable = false,
    renderDetail,
    groupingType = GroupingType.None,
    canReorderRows = false,
    onMoveRow,
    onMoveRowWithGrouping,
    hasSelectableRows = false,
    canSelectRow,
    onSortChange,
    maxHeight = 1000,
    renderToolbarContent,
    emptyState,
    autoFreezeFirstColumn,
    sticky = true,
    additionalFooterRows,
}) => {
    /* As we add more props to be consumed, be sure to use the defaults
    static defaultProps = {
        stickyHeaders: false,
        onGroupChange: () => {},
        isReadOnly: false,
    };*/

    const saving = useRef(false);

    // Account for height of non-table elements content
    // 48px main nav, 72 px estimates header, 48px to 88px header button row, 36px table header, 36px table footer
    // Needs 10px extra padding or the second scrollbar shows while zooming out.
    const heightOfTable = maxHeight;

    const [tableRef, onTableRefChange] = useResizeObserver<HTMLDivElement>((resizeEntry) => {
        if (
            resizeEntry &&
            scrollable &&
            (size.height !== resizeEntry.contentRect.height ||
                size.width !== resizeEntry.contentRect.width)
        ) {
            setSize({
                height: resizeEntry.contentRect.height,
                width: resizeEntry.contentRect.width,
            });
        }
    });

    const [size, setSize] = useState({ width: 700, height: heightOfTable });
    const [prevJumpId, setPrevJumpId] = useState<number>();
    const [quickFindItemIndex, setQuickFindItemIndex] = useState<number>(0);

    /** Calling initTop 1 to force an onScroll event within the virtual table. On load the virtual
     * table package doesn't calculate each item's height: it assumes they all have the same height
     * as the first row in the table. Their onScroll event however handles dynamic height and sets
     * the virtual table div's height to the correct amount, preventing display issues.
     *
     * When size.height changes, the VT context re-creates and resets its internal row_height
     * tracking. The useEffect below calls scrollTo(0) after re-creation to force the VT to
     * recalculate row visibility, preventing the first row from being hidden (Bug 260073,
     * Bug 265911). The isInitialHeight guard skips the scroll on mount.
     * */
    const [vt, setVT, vtRef] = useVT(() => ({ scroll: { y: size.height }, initTop: 1 }), []);

    const isInitialHeight = useRef(true);
    useEffect(() => {
        if (isInitialHeight.current) {
            isInitialHeight.current = false;
            return;
        }
        vtRef?.current?.scrollTo(0);
    }, [size.height, vtRef]);

    const getDataItemFieldPath = useCallback(
        (
            dataItem: ILineItemRow | ILineItemGroup,
            data: (ILineItemRow | ILineItemGroup)[],
        ): string => {
            const index =
                data.findIndex(
                    (item: ILineItemRow | ILineItemGroup<ILineItemRow>) => item.id === dataItem.id,
                ) + indexOffset;
            return `${dataField}[${index}]`;
        },
        [dataField, indexOffset],
    );

    const setLineItemTouched = useCallback(
        (lineItemIndex: number, fieldsToSetTouched: string[], touched: boolean | null) => {
            let updateTouchedFields = touchedFields?.[lineItemIndex] ?? {};

            if (touched !== null) {
                // sometimes this can be set to a boolean, which doesn't do anything. update it to be an object.
                if (typeof updateTouchedFields === "boolean") {
                    updateTouchedFields = {};
                }

                fieldsToSetTouched.forEach((x) => {
                    updateTouchedFields[x] = touched;
                });
            }
            const updatedTouched: FormikTouched<any>[] = Object.assign([], touchedFields ?? [], {
                // We saw strange behavior where having touched.lineItems[index] = {a: false, b: false, etc...} with all items false
                // was still triggering an outline on the costCodeId field. So when we clear validation, we want to actually
                // Set the touched to null there.

                // If you consider refactoring this, test the following:
                // 1. Add a line item
                // 2. Click off before specifying a cost code in order to get validation
                // 3. pick a cost code, and save that line item
                // 4. Add another line item, notice that the cost code field is already "Touched"
                // 5. Also, we aren't showing validation messages if you click off.
                [lineItemIndex]: touched === null ? null : updateTouchedFields,
            });
            setLineItemsTouched(updatedTouched);
        },
        [setLineItemsTouched, touchedFields],
    );

    const setCostGroupReadonly = useCallback(
        async (costGroupBeingEdited: ILineItemGroup<ILineItemRow>): Promise<boolean> => {
            // check current line item to see if there are any errors
            const costGroupPath = getDataItemFieldPath(costGroupBeingEdited, data);
            const index = data.findIndex((x) => x.id === costGroupBeingEdited.id) + indexOffset;
            const errors = lineItemErrors?.[index] ?? {};
            if (Object.keys(errors).length === 0) {
                setFieldValue(`${costGroupPath}._isInEdit`, false, false);
                saving.current = false;
                if (onGroupEditFinished) {
                    const lineItemPath = getDataItemFieldPath(costGroupBeingEdited, data);
                    await onGroupEditFinished(costGroupBeingEdited, lineItemPath);
                }
                return true;
            }
            saving.current = false;
            return false;
        },
        [
            data,
            getDataItemFieldPath,
            indexOffset,
            lineItemErrors,
            setFieldValue,
            onGroupEditFinished,
        ],
    );

    // returns an empty list if unable to set line item readonly
    const setLineItemReadonly = useCallback(async (): Promise<
        (ILineItemRow | ILineItemGroup)[]
    > => {
        if (saving.current) {
            return [];
        }

        const lineItemBeingEdited = data.find((x) => x._isInEdit && isLineItemRow(x)) as
            | ILineItemRow
            | undefined;
        const costGroupBeingEdited = data.find((x) => isLineItemGroupRow(x) && x._isInEdit) as
            | ILineItemGroup<ILineItemRow>
            | undefined;

        if (!lineItemBeingEdited && !costGroupBeingEdited) {
            return data;
        }

        if (costGroupBeingEdited) {
            return (await setCostGroupReadonly(costGroupBeingEdited)) ? data : [];
        } else if (lineItemBeingEdited) {
            const index = data.findIndex((x) => x.id === lineItemBeingEdited.id) + indexOffset;
            if (!isLineItemDirty(lineItemBeingEdited)) {
                // Nothing changed. Just take the record out of edit mode and call it good.
                const lineItemPath = getDataItemFieldPath(lineItemBeingEdited, data);
                setFieldValue(lineItemPath, { ...lineItemBeingEdited, _isInEdit: false }, false);
                return data;
            } else {
                saving.current = true;
                const isValid = await onValidateLineItem(index);
                if (isValid) {
                    let updatedList: (ILineItemRow | ILineItemGroup)[] = [];
                    if (onLineItemEditFinished) {
                        // Preserve the action row to not let indices get mismatched
                        let actionRow = null;
                        if (isLineItemActionRow(data[0])) {
                            actionRow = data[0];
                        }

                        const lineItemPath = getDataItemFieldPath(lineItemBeingEdited, data);
                        updatedList = await onLineItemEditFinished(
                            lineItemBeingEdited,
                            lineItemPath,
                        );

                        if (actionRow && !isLineItemActionRow(updatedList[0])) {
                            updatedList = [actionRow, ...updatedList];
                        }
                    }
                    setLineItemTouched(index, [], null);
                    saving.current = false;
                    return updatedList;
                } else {
                    // To show the errors, go mark all keys in error as touched.
                    setLineItemTouched(index, Object.keys(lineItemBeingEdited), true);
                    saving.current = false;
                    return [];
                }
            }
        }
        saving.current = false;
        return [];
    }, [
        data,
        getDataItemFieldPath,
        indexOffset,
        isLineItemDirty,
        onLineItemEditFinished,
        onValidateLineItem,
        setCostGroupReadonly,
        setFieldValue,
        setLineItemTouched,
    ]);

    const handleEnter = useCallback(
        (e: KeyboardEvent) => {
            const targetElement = e.target as HTMLElement;
            if (targetElement?.hasAttribute("data-action-row")) {
                // If we are on an action row, we want the action row's event to be executed
                e.preventDefault();
            } else if (
                targetElement.nodeName !== "TEXTAREA" &&
                targetElement.nodeName !== "BUTTON"
            ) {
                // If we are on a textarea we want the "Enter" key to result in a new line
                // If we are on a button, we want the default button action (onClick) to be executed
                handleEnterKeyPressed(e, setLineItemReadonly);
            }
        },
        [setLineItemReadonly],
    );

    const handleClickOutsideOfGrid = useCallback(
        async (e: MouseEvent) => {
            const lineItemDiv = tableRef.current;
            const targetElement = e.target as Element;
            if (
                lineItemDiv &&
                !lineItemDiv.contains(targetElement) &&
                !targetElement.closest("[data-action-button]") &&
                !targetElement.closest("[data-delete-confirm]") &&
                !targetElement.classList.contains("ant-modal-wrap")
            ) {
                await setLineItemReadonly();
            }
        },
        [tableRef, setLineItemReadonly],
    );

    useEffect(() => {
        let isDetailHoveringActive = false;
        const handleDetailMouseOver = (e: Event) => {
            const targetElement = e.target as HTMLElement;
            const row = targetElement.closest("tr.expandRow");

            if (row && !isDetailHoveringActive) {
                (row.previousSibling as HTMLElement).classList.add("hovering");
                isDetailHoveringActive = true;
            }

            if (!row && isDetailHoveringActive) {
                const containerDiv = document.querySelector(`.${containerName}`);
                containerDiv?.querySelectorAll(".detailRow.hovering").forEach((row) => {
                    row.classList.remove("hovering");
                });
                isDetailHoveringActive = false;
            }
        };

        window.addEventListener("click", handleClickOutsideOfGrid);
        if (renderDetail) {
            window.addEventListener("mouseover", handleDetailMouseOver);
        }

        const lineItemDiv = tableRef.current;
        if (lineItemDiv) {
            lineItemDiv.addEventListener("keypress", handleEnter);
        }

        return () => {
            // on unmount, remove any of the bound listeners
            window.removeEventListener("click", handleClickOutsideOfGrid);
            if (renderDetail) {
                window.removeEventListener("mouseover", handleDetailMouseOver);
            }

            if (lineItemDiv) {
                lineItemDiv.removeEventListener("keypress", handleEnter);
            }
        };
    }, [tableRef, containerName, handleClickOutsideOfGrid, handleEnter, renderDetail]);

    const showRowInErrorFunction = useCallback(
        (errors: FormikErrors<any> | undefined, touched: FormikTouched<any> | undefined) => {
            if (errors && touched) {
                const errorKeys = Object.keys(errors);
                return errorKeys.some((key) => touched[key]);
            }
            return false;
        },
        [],
    );

    const showRowInError = useCallback(
        (rowIndex: number) => {
            const errors = (lineItemErrors ?? [])[rowIndex];
            const touched = (touchedFields ?? [])[rowIndex];
            const hasErrorAndHasBeenTouched = showRowInErrorFunction(errors, touched);

            return hasErrorAndHasBeenTouched;
        },
        [lineItemErrors, showRowInErrorFunction, touchedFields],
    );

    const putRowInEditMode = useCallback(
        async (row: LineItem, field?: string) => {
            let canSetEditMode = true;
            let updatedData: (ILineItemRow | ILineItemGroup)[] = data;
            const lineItemInEdit = data.find((x) => x._isInEdit);
            if (lineItemInEdit && lineItemInEdit.id !== row.id) {
                updatedData = await setLineItemReadonly();
                canSetEditMode = updatedData.length > 0;
            }
            if (!isLineItemGroupRow(row) && canSetEditMode) {
                if (isEditable && row.isEditable && row.id !== lineItemInEdit?.id) {
                    const val = { ...row, _isInEdit: true };
                    val.autoFocusedField = field;
                    setFieldValue(getDataItemFieldPath(row, updatedData), val, false);
                }
            } else if (
                canSetEditMode &&
                isEditable &&
                row.id !== lineItemInEdit?.id &&
                row.id !== UnassignedCostGroupId
            ) {
                const val = { ...row, _isInEdit: true };
                val.autoFocusedField = field;
                setFieldValue(getDataItemFieldPath(row, updatedData), val, false);
            }
        },
        [data, getDataItemFieldPath, isEditable, setFieldValue, setLineItemReadonly],
    );

    const expandable = useMemo<ExpandableConfig<LineItem> | undefined>(() => {
        if (groupingType !== GroupingType.None) {
            return {
                expandIconColumnIndex: 0,
                expandedRowRender: () => undefined,
                expandIcon: (props) => {
                    if (isLineItemActionRow(props.record)) {
                        return <></>;
                    }
                    const hasReorderableItems =
                        canReorderRows && isEditable && hasMoreThanOneOrderableFormatItems(data);

                    return (
                        <div className="itemIconContainer">
                            {hasReorderableItems && <DragAndDropHandle />}
                            {isLineItemGroupRow(props.record) && props.record.items.length > 0 && (
                                <BTButtonDeprecated
                                    icon={
                                        props.expanded ? (
                                            <BTIconCaretDownOutlined className="collapseExpandIcon" />
                                        ) : (
                                            <BTIconCaretRightOutlined className="collapseExpandIcon" />
                                        )
                                    }
                                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                                        props.onExpand(props.record, e)
                                    }
                                    type="link"
                                    data-testid="btnExpandCollapse"
                                />
                            )}
                        </div>
                    );
                },
                onExpand: (expanded, row) => {
                    const path = getDataItemFieldPath(row, data);
                    setFieldValue(`${path}.isExpanded`, expanded, false);
                },
                expandedRowKeys: data.filter((x) => x.isExpanded).map((y) => y.id),
                expandedRowClassName: () => "expandedRow",
                columnWidth: 36,
            };
        } else {
            if (!renderDetail && (!isEditable || !canReorderRows)) {
                return undefined;
            }

            const hasReorderableItems =
                canReorderRows && isEditable && hasMoreThanOneOrderableFormatItems(data);
            const columnWidth = renderDetail ? 36 : 18;
            const adjustedColumnWidth = hasReorderableItems ? columnWidth + 10 : columnWidth;

            return {
                expandIconColumnIndex: 0,
                expandedRowRender: () => undefined,
                rowExpandable: (record) => isLineItemRow(record),
                expandIcon: (props) => {
                    if (isLineItemActionRow(props.record) || isLineItemExpandRow(props.record)) {
                        return <></>;
                    }

                    return (
                        <div className="itemIconContainer">
                            {hasReorderableItems && <DragAndDropHandle />}
                            {renderDetail && !props.record._isInEdit && (
                                <BTButtonDeprecated
                                    icon={
                                        props.expanded ? (
                                            <BTIconCaretDownOutlined className="collapseExpandIcon" />
                                        ) : (
                                            <BTIconCaretRightOutlined className="collapseExpandIcon" />
                                        )
                                    }
                                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                                        props.onExpand(props.record, e)
                                    }
                                    type="link"
                                    data-testid={`lineItemCaretIcon[${data.indexOf(props.record)}]`}
                                />
                            )}
                        </div>
                    );
                },
                onExpand: (expanded, row) => {
                    const path = getDataItemFieldPath(row, data);
                    setFieldValue(`${path}.isExpanded`, expanded, false);
                },
                expandedRowKeys: data.filter((x) => x.isExpanded || x._isInEdit).map((y) => y.id),
                expandedRowClassName: () => "expandedRow",
                // expand icon getting cut off when expanded in edit mode, this fixes that
                columnWidth: adjustedColumnWidth,
            };
        }
    }, [
        canReorderRows,
        data,
        getDataItemFieldPath,
        groupingType,
        renderDetail,
        setFieldValue,
        isEditable,
    ]);

    const columnsDisplay: IBTColumnType<LineItem>[] = useMemo(() => {
        const allColumns = columnsList;

        return allColumns
            .sort((a, b) => a.order - b.order)
            .map((col, colIndex) => {
                let fixedColumn: "left" | "right" | true | undefined = undefined;

                const shouldFreezeFirstColumn = autoFreezeFirstColumn ?? true;
                if ((colIndex === 0 && shouldFreezeFirstColumn) || col.isFrozen) {
                    fixedColumn = "left";
                }

                if (colIndex === allColumns.length - 1 && col.isFrozen) {
                    fixedColumn = "right";
                }

                const title = (
                    <>
                        {col.name}
                        {col.toolTipHeader && (
                            <BTPopover
                                className="BTPopoverInlineBlock"
                                content={col.toolTipHeader}
                            />
                        )}
                    </>
                );

                const result: IBTColumnType<LineItem> = {
                    key: col.id,
                    align: col.align,
                    className: col.className,
                    title: title,
                    width: col.width || col.minWidth,
                    minWidth: col.minWidth,
                    fixed: fixedColumn,
                    sorter: onSortChange && col.isSortable,
                    sortDirections: ["ascend", "descend", "ascend"], // when set like this, will switch between ascend and descend without going back to unsorted, which isn't supported
                    ellipsis: col.ellipsis,
                    onCell: (record) => ({
                        onClick: async (e: React.MouseEvent) => {
                            e.stopPropagation();
                            const isNotActionOrTitleAndCostCode =
                                col.columnType !== GridLineItemColumnType.action &&
                                col.columnType !== GridLineItemColumnType.worksheetTitleAndCostCode;

                            const isGrouped =
                                (!isLineItemGroupRow(record) && record.isEditable) ||
                                (isLineItemGroupRow(record) && groupingType !== GroupingType.None);

                            const canClickIntoCell =
                                isEditable && isGrouped && isNotActionOrTitleAndCostCode;
                            if (record._isInEdit || record.isExpanded) {
                                // on the worksheet, if a line item is in edit mode, don't trigger anything when clicking the editable cells
                                return;
                            }

                            canClickIntoCell
                                ? await putRowInEditMode(record, col.jsonKey)
                                : await setLineItemReadonly();
                        },
                        className: cn({
                            editing: record._isInEdit,
                        }),
                    }),
                    render: (_: any, record: LineItem | IGridLineItemActionRow, index: number) => {
                        const isFirstColumn = colIndex === 0;
                        const isActionColumn = col.columnType === GridLineItemColumnType.action;

                        // header row for "Add Category" and other parent actions
                        if (isLineItemActionRow(record)) {
                            const iconClassNames = cn("actionIcon", {
                                hasRenderDetail: renderDetail,
                            });

                            const actionButton = (
                                <>
                                    {/* if this is the first column, and there is an onClick handler, render a button */}
                                    {isFirstColumn && record.onClick && (
                                        <BTButtonDeprecated
                                            type="link"
                                            data-testid="action-button"
                                            className="actionButton"
                                            data-action-button
                                            icon={
                                                <BTIconPlusCircleFilled
                                                    className={iconClassNames}
                                                />
                                            }
                                            onClick={async () => {
                                                if (await setLineItemReadonly()) {
                                                    record.onClick!();
                                                }
                                            }}
                                        >
                                            {record.rowText}
                                        </BTButtonDeprecated>
                                    )}
                                    {/* if first column, and no onClick handler, and has default row content */}
                                    {isFirstColumn &&
                                        !record.onClick &&
                                        record.defaultRowContent && (
                                            <div className="defaultRowContent">
                                                {record.defaultRowContent}
                                            </div>
                                        )}
                                </>
                            );

                            // need to account for the expand and select columns ant adds
                            let colSpanLength = allColumns.length;
                            if (expandable) {
                                colSpanLength++;
                            }
                            if (hasSelectableRows) {
                                colSpanLength++;
                            }

                            const actionRow = {
                                children: actionButton,
                                props: {
                                    colSpan: isFirstColumn ? colSpanLength : 0,
                                    "data-action-row": isFirstColumn,
                                    className: record.isSticky
                                        ? "text-left StickyActionRow"
                                        : "text-left",
                                },
                            };

                            return actionRow;
                        }

                        if (isLineItemExpandRow(record)) {
                            return {
                                children: renderDetail!({
                                    dataItem: (record as any).relatedLineItem,
                                    onCellClick: (fieldPath: string, fieldName: string) =>
                                        putRowInEditMode(
                                            (record as any).relatedLineItem,
                                            fieldName,
                                        ),
                                    onBlur: () => {},
                                }),
                                props: {
                                    colSpan: isFirstColumn ? columnsDisplay.length : 0,
                                    "data-expand-row": isFirstColumn,
                                },
                            };
                        }

                        const fieldPath = getDataItemFieldPath(record, data);
                        const field = {
                            full: `${fieldPath}.${col.jsonKey}`,
                            path: fieldPath,
                            name: col.jsonKey,
                        };

                        // Group row that contains child elements
                        if (isLineItemGroupRow(record)) {
                            if (onRenderGroupRow) {
                                const isGroupedData = groupingType !== GroupingType.None;

                                // we need to add a column for the draggable and collapse icons with ant v4. If it has those icons visible, the title column will be the second column
                                const isTitleColumn = colIndex === 0;

                                const groupCellData = onRenderGroupRow({
                                    item: record,
                                    isEditable: isEditable,
                                    cellProps: {
                                        dataIndex: index,
                                        dataItem: record,
                                        style: {},
                                    },
                                    column: col,
                                    field: field,
                                    handleCostGroupClick: async (
                                        fieldPath: string,
                                        groupId: number,
                                        fieldName?: string,
                                    ) => {
                                        await putRowInEditMode(record, fieldName);
                                    },
                                    isActionColumn: isActionColumn,
                                    isTitleColumn: isTitleColumn,
                                    rowHasErrors: false,
                                    setLineItemReadonly: setLineItemReadonly,
                                });

                                const hasOwnerPriceCell = allColumns.some(
                                    (x) => x.columnType === GridLineItemColumnType.ownerPrice,
                                );
                                const totalDataColumnsCount = allColumns.filter(
                                    (x) =>
                                        x.columnType !== GridLineItemColumnType.action &&
                                        x.columnType !== GridLineItemColumnType.empty,
                                ).length;

                                const columnSpan = getColumnSpan(
                                    hasOwnerPriceCell ?? false,
                                    totalDataColumnsCount,
                                    isGroupedData,
                                    isTitleColumn,
                                    colIndex,
                                );
                                return {
                                    children: groupCellData,
                                    props: {
                                        colSpan: columnSpan,
                                        className: cn({
                                            "text-left": isTitleColumn,
                                            groupRowTitleCell: isGroupedData && isTitleColumn,
                                        }),
                                        "data-testid": `cell-${col.jsonKey}`,
                                    },
                                };
                            }
                            return null;
                        }
                        const indexInState = data.findIndex((x) => x._isInEdit);
                        const lineItemInEdit = data[indexInState];
                        const isInEdit = record.id === lineItemInEdit?.id;
                        const Cell = col.Cell;

                        const cellValue = col.getCellValue
                            ? col.getCellValue(record)
                            : record[col.jsonKey as keyof ILineItemRow];

                        const canEdit =
                            record.isEditable && (col.canEdit ? col.canEdit(record) : false);

                        const tdContent = (
                            <Cell
                                cellValue={cellValue}
                                autoFocus={record.autoFocusedField === col.jsonKey}
                                column={col as any}
                                field={field}
                                entityId={record.id}
                                isInEdit={isInEdit}
                                onBlur={() => {}}
                                canEdit={canEdit}
                                onChange={(field, value) =>
                                    setFieldValue(field, value, showRowInError(indexInState))
                                }
                                onFocus={async () => {
                                    const canClickIntoCell =
                                        ((!isLineItemGroupRow(record) && record.isEditable) ||
                                            (isLineItemGroupRow(record) &&
                                                groupingType !== GroupingType.None)) &&
                                        col.columnType !== GridLineItemColumnType.action &&
                                        col.columnType !==
                                            GridLineItemColumnType.worksheetTitleAndCostCode;
                                    canClickIntoCell &&
                                        (await putRowInEditMode(record, col.jsonKey));
                                }}
                                setRowTouched={() => {}}
                                rowIndex={index}
                            />
                        );
                        return {
                            children: tdContent,
                            props: {
                                "data-testid": `cell-${col.jsonKey}`,
                            },
                        };
                    },
                };
                return result;
            });
    }, [
        columnsList,
        onSortChange,
        isEditable,
        groupingType,
        putRowInEditMode,
        setLineItemReadonly,
        getDataItemFieldPath,
        data,
        renderDetail,
        expandable,
        hasSelectableRows,
        onRenderGroupRow,
        setFieldValue,
        showRowInError,
        autoFreezeFirstColumn,
    ]);

    const style = useMemo(() => {
        return {
            maxHeight: scrollable ? heightOfTable : undefined,
        };
    }, [scrollable, heightOfTable]);

    const lineItemCount = useRef(0);

    const dataItems = useMemo(() => {
        if (groupingType !== GroupingType.None) {
            const dataArray = data.flatMap((li) => {
                // remove the line items if there is nothing to display
                if (
                    isLineItemGroupRow(li) &&
                    groupingType !== GroupingType.GroupsWithoutLineItems &&
                    li.isExpanded
                ) {
                    const subLineItems = li.items.map((subLi) => {
                        return { ...subLi, isEditable: false };
                    });
                    return [li, ...subLineItems];
                } else {
                    return li;
                }
            });
            const previousLineItemCount = lineItemCount.current;
            // use data instead of dataArray, to avoid incorrectly scrolling when a group is collapsed
            const newLineItemCount = data.length;
            lineItemCount.current = newLineItemCount;
            if (scrollable && vtRef?.current && newLineItemCount === previousLineItemCount + 1) {
                // when a new group is added, keep scroll at top
                vtRef?.current.scrollToIndex(0);
            } else if (lineItemToJumpTo && prevJumpId !== lineItemToJumpTo.uniqueJumpId) {
                setPrevJumpId(lineItemToJumpTo.uniqueJumpId);
                setQuickFindItemIndex(dataArray.findIndex((x) => x.id === lineItemToJumpTo.id));
            }

            return dataArray;
        } else {
            const dataArray: (ILineItemRow | ILineItemGroup)[] = [];
            data.forEach((li) => {
                dataArray.push(li);
                if (isLineItemRow(li) && renderDetail) {
                    // always add in the expand rows to handle an issue with our virtual scroll library that causes the scrollbar to display prematurely. If it is not expanded, we will set a display: none on the row
                    dataArray.push({
                        relatedLineItem: li,
                        isExpandRow: true,
                        id: `${li.id}-expanded`,
                        isExpanded: li.isExpanded || li._isInEdit,
                    } as any);
                }
            });
            const previousLineItemCount = lineItemCount.current;
            const newLineItemCount = dataArray.length;
            lineItemCount.current = newLineItemCount;
            if (scrollable && vtRef?.current) {
                if (renderDetail && newLineItemCount === previousLineItemCount + 2) {
                    // for the non-estimate LICs, each new line item adds two lines, and only are added at the bottom of the page.
                    vtRef?.current.scrollToIndex(dataArray.length - 1);
                } else if (!renderDetail && newLineItemCount === previousLineItemCount + 1) {
                    // this will be the worksheet for either lead proposals or estimates. Since line items can be added to any group, scroll to the group where the line item is being added
                    let newestAddedIndex = 0;
                    const newestAddedInEditIndex = dataArray.findIndex((x) => x._isInEdit);
                    const newestAddedIsDirtyIndex = dataArray.findIndex((x) => x._isDirty);

                    if (newestAddedInEditIndex >= 0) {
                        newestAddedIndex = newestAddedInEditIndex;
                    } else if (newestAddedIsDirtyIndex >= 0) {
                        newestAddedIndex = newestAddedIsDirtyIndex;
                    } else {
                        newestAddedIndex = dataArray.reduce(
                            (previousValue, currentValue, index) =>
                                dataArray[previousValue].id > currentValue.id
                                    ? index
                                    : previousValue,
                            0,
                        );
                    }

                    vtRef?.current.scrollToIndex(newestAddedIndex - 1);
                } else if (lineItemToJumpTo && prevJumpId !== lineItemToJumpTo.uniqueJumpId) {
                    setPrevJumpId(lineItemToJumpTo.uniqueJumpId);
                    setQuickFindItemIndex(dataArray.findIndex((x) => x.id === lineItemToJumpTo.id));
                }
            }
            return dataArray;
        }
    }, [groupingType, data, scrollable, vtRef, renderDetail, lineItemToJumpTo, prevJumpId]);

    useEffect(() => {
        const tableBody = document.querySelector(".LineItemContainer .ant-table-body");
        const currentScroll = tableBody?.scrollHeight ?? 0;
        if (lineItemToJumpTo && prevJumpId && tableBody) {
            scrollToQuickFindItem(
                lineItemToJumpTo,
                quickFindItemIndex,
                vtRef,
                tableBody,
                currentScroll,
            );
        }
    }, [lineItemToJumpTo, prevJumpId, quickFindItemIndex, vtRef]);

    const moveRow = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            if (onMoveRow) {
                onMoveRow(dragIndex, hoverIndex);
            } else {
                const reorderedLineItems = update(
                    dataItems,
                    moveDragItemToHoverPosition(dataItems, dragIndex, hoverIndex),
                ).filter((x) => !isLineItemExpandRow(x) && !isLineItemActionRow(x));

                setFieldValue(dataField, reorderedLineItems, false);
            }
        },
        [dataItems, dataField, setFieldValue, onMoveRow],
    );

    const moveRowWithGrouping = useCallback(
        (draggedRow: IDragProps, hoveredRow: IAntGridRowProps) => {
            if (onMoveRowWithGrouping) {
                onMoveRowWithGrouping(draggedRow, hoveredRow);
            } else {
                const dataWithoutActionRow: ILineItemGroup[] = data.filter((x) =>
                    isLineItemGroupRow(x),
                );
                if (hoveredRow.rowType === RowType.Group) {
                    // if we are dragging a group, we only care if we are hovering over a group. This should be handled inside of LineItemDraggableRow, but double checking here
                    if (draggedRow.type === RowType.Group) {
                        const dragIndex = dataWithoutActionRow.findIndex(
                            (x) => x.id === draggedRow.id,
                        );
                        const hoverIndex = dataWithoutActionRow.findIndex(
                            (x) => x.id === hoveredRow.rowId,
                        );

                        const updatedData = update(
                            dataWithoutActionRow,
                            moveDragItemToHoverPosition(
                                dataWithoutActionRow,
                                dragIndex,
                                hoverIndex,
                            ),
                        );
                        setFieldValue(dataField, updatedData, false);
                    } else if (draggedRow.type === RowType.LineItem) {
                        // dragging a line item over a group
                        const draggingLineItemCategoryIndex = dataWithoutActionRow.findIndex(
                            (x) => x.items.find((li) => li.id === draggedRow.id) !== undefined,
                        );
                        const hoveredCategoryIndex = dataWithoutActionRow.findIndex(
                            (x) => x.id === hoveredRow.rowId,
                        );
                        if (draggingLineItemCategoryIndex === hoveredCategoryIndex) {
                            return;
                        }

                        const updatedData = [...dataWithoutActionRow];

                        const hoveredCategory = { ...updatedData[hoveredCategoryIndex] };
                        const oldCategory = { ...updatedData[draggingLineItemCategoryIndex] };
                        const dragLineItem = oldCategory.items.find((x) => x.id === draggedRow.id)!;

                        oldCategory.items = oldCategory.items.filter((x) => x.id !== draggedRow.id);
                        hoveredCategory.items = [dragLineItem, ...hoveredCategory.items];

                        updatedData[hoveredCategoryIndex] = hoveredCategory;
                        updatedData[draggingLineItemCategoryIndex] = oldCategory;

                        setFieldValue(dataField, updatedData);
                    }
                } else if (
                    hoveredRow.rowType === RowType.LineItem &&
                    draggedRow.type === RowType.LineItem // Need to handle two cases here: dragging over a line item, and dragging over a group
                ) {
                    const hoveredLineItemCategoryIndex = dataWithoutActionRow.findIndex(
                        (x) => x.items.find((li) => li.id === hoveredRow.rowId) !== undefined,
                    );
                    const draggingLineItemCategoryIndex = dataWithoutActionRow.findIndex(
                        (x) => x.items.find((li) => li.id === draggedRow.id) !== undefined,
                    );

                    if (draggingLineItemCategoryIndex === hoveredLineItemCategoryIndex) {
                        // line items are a part of the same group, just need to move items around in category
                        const sharedCategory = dataWithoutActionRow[draggingLineItemCategoryIndex];
                        const dragLineItemIndex = sharedCategory.items.findIndex(
                            (x) => x.id === draggedRow.id,
                        );
                        const hoveredLineItemIndex = sharedCategory.items.findIndex(
                            (x) => x.id === hoveredRow.rowId,
                        );
                        const dragLineItem = sharedCategory.items[dragLineItemIndex];

                        const updatedLineItemOrder = update(sharedCategory.items, {
                            $splice: [
                                [dragLineItemIndex, 1], // remove the dragging item from the list
                                [hoveredLineItemIndex, 0, dragLineItem], // insert it before the hover index
                            ],
                        });

                        const categoryField = getDataItemFieldPath(sharedCategory, data);
                        setFieldValue(categoryField, {
                            ...sharedCategory,
                            items: updatedLineItemOrder,
                        });
                    } else {
                        // line items are in different groups.

                        const updatedData = [...dataWithoutActionRow];
                        const categoryLineItemMovingTo = {
                            ...updatedData[hoveredLineItemCategoryIndex],
                        };

                        const oldCategory = { ...updatedData[draggingLineItemCategoryIndex] };
                        const dragLineItemIndex = oldCategory.items.findIndex(
                            (x) => x.id === draggedRow.id,
                        );

                        const dragLineItem = oldCategory.items[dragLineItemIndex];

                        oldCategory.items = oldCategory.items.filter((x) => x.id !== draggedRow.id);

                        categoryLineItemMovingTo.items = [
                            ...categoryLineItemMovingTo.items,
                            dragLineItem,
                        ];

                        updatedData[hoveredLineItemCategoryIndex] = categoryLineItemMovingTo;
                        updatedData[draggingLineItemCategoryIndex] = oldCategory;

                        setFieldValue(dataField, updatedData);
                    }
                }
            }
        },
        [data, setFieldValue, dataField, getDataItemFieldPath, onMoveRowWithGrouping],
    );

    const onRow = useCallback(
        (row: LineItem, index: number | undefined): React.HTMLAttributes<HTMLElement> => {
            const rowType = getRowType(row);
            const isActionRowOrExpandRow = [RowType.Action, RowType.Expand].includes(rowType);
            const dataIndex = data.indexOf(row) + indexOffset;
            const unassignedRow = isLineItemGroupRow(row) && row.id === UnassignedCostGroupId;
            const hasReorderableItems = canReorderRows && hasMoreThanOneOrderableFormatItems(data);
            const highlightable = row.id === lineItemToJumpTo?.id;
            const jumpId =
                highlightable && lineItemToJumpTo ? `Jump${lineItemToJumpTo.id}` : undefined;

            return {
                index,
                className: cn(
                    jumpId,
                    {
                        categoryRow: !isActionRowOrExpandRow && isLineItemGroupRow(row),
                        verticallyCenteredContent:
                            !isActionRowOrExpandRow &&
                            isLineItemGroupRow(row) &&
                            groupingType === GroupingType.None,
                        detailRow: !isActionRowOrExpandRow && !isLineItemGroupRow(row),
                        editing:
                            !isActionRowOrExpandRow && data.find((x) => x._isInEdit)?.id === row.id,
                        errors: showRowInError(dataIndex),
                        isExpanded: row.isExpanded || (renderDetail && row._isInEdit),
                        isSelected: row.isSelected,
                        highlightable,
                        actionRow: rowType === RowType.Action,
                        unassignedRow,
                        expandRowHidden: isLineItemExpandRow(row) && !row.isExpanded,
                    },
                    rowType === RowType.Action && (row as any).borderStyle,
                ),
                ...(scrollable && vt
                    ? {
                          moveRow: groupingType === GroupingType.None ? moveRow : undefined,
                          moveRowWithGrouping:
                              groupingType !== GroupingType.None ? moveRowWithGrouping : undefined,
                          canReorder:
                              canReorderRows &&
                              hasReorderableItems &&
                              row.id !== UnassignedCostGroupId &&
                              [RowType.LineItem, RowType.Group].includes(rowType),
                          rowType: rowType,
                          rowId: row.id,
                          isInEdit:
                              row._isInEdit ||
                              (isLineItemExpandRow(row) && (row as any).relatedLineItem._isInEdit),
                      }
                    : {}),
            } as IAntGridRowProps;
        },
        [
            data,
            indexOffset,
            groupingType,
            showRowInError,
            renderDetail,
            moveRowWithGrouping,
            moveRow,
            lineItemToJumpTo,
            canReorderRows,
            scrollable,
            vt,
        ],
    );

    const handleSelectionChange = (row: ILineItemGroup | ILineItemRow, selected: boolean) => {
        if (
            groupingType === GroupingType.GroupedLineItems ||
            groupingType === GroupingType.GroupsWithoutLineItems
        ) {
            // logic specific to proposal format, where the line items are nested under the category
            if (isLineItemGroupRow(row)) {
                const updatedCategory = {
                    ...row,
                    isSelected: selected,
                    items: row.items.map((x) => {
                        return { ...x, isSelected: selected };
                    }),
                };

                const categoryPath = getDataItemFieldPath(row, data);
                setFieldValue(categoryPath, updatedCategory, false);
            } else if (isLineItemRow(row)) {
                const relatedCategory = data.find(
                    (x) =>
                        isLineItemGroupRow(x) && x.items.find((y) => y.id === row.id) !== undefined,
                )! as ILineItemGroup;
                const groupFieldPath = getDataItemFieldPath(relatedCategory, data);

                const updatedLineItems = relatedCategory.items.map((x) =>
                    x.id === row.id ? { ...x, isSelected: selected } : x,
                );

                // we need to update the value of the category's isSelectedValue if one of its line items are deselected, or if all line items are selected
                const updatedGroupSelectValue = updatedLineItems.every((x) => x.isSelected);
                setFieldValue(
                    groupFieldPath,
                    {
                        ...relatedCategory,
                        isSelected: updatedGroupSelectValue,
                        items: updatedLineItems,
                    },
                    false,
                );
            }
        } else {
            if (isLineItemRow(row)) {
                const path = getDataItemFieldPath(row, data);
                setFieldValue(`${path}.isSelected`, selected, false);

                // if a line item is selected, we also need to see update the group row's checked value
                const lineItemSelectedStates: SelectedStates = {};
                data.filter((x) => isLineItemRow(x)).forEach(
                    (li) => (lineItemSelectedStates[li.id] = li.isSelected),
                );
                lineItemSelectedStates[row.id] = selected;

                const relatedGroup: ILineItemGroup = data.find(
                    (x) => isLineItemGroupRow(x) && x.id === (row as any).costCategoryId,
                ) as ILineItemGroup;

                if (relatedGroup) {
                    // if a line item is selected, we also need to see update the group row's checked value
                    const lineItemSelectedStates: SelectedStates = {};
                    data.filter((x) => isLineItemRow(x)).forEach(
                        (li) => (lineItemSelectedStates[li.id] = li.isSelected),
                    );
                    lineItemSelectedStates[row.id] = selected;

                    const allLineItemsSelected = relatedGroup.items.every(
                        (x) => lineItemSelectedStates[x.id],
                    );
                    if (allLineItemsSelected !== relatedGroup.isSelected) {
                        const groupPath = getDataItemFieldPath(relatedGroup, data);
                        setFieldValue(`${groupPath}.isSelected`, allLineItemsSelected, false);
                    }
                }
            } else if (isLineItemGroupRow(row)) {
                const objectMap: SelectedStates = {};
                objectMap[row.id] = selected;
                row.items.forEach((x) => (objectMap[x.id] = selected));

                const updatedDataItems = dataItems.map((x) => {
                    return { ...x, isSelected: objectMap[x.id] ?? x.isSelected };
                });
                setFieldValue(dataField, updatedDataItems, false);
            }
        }
    };

    const handleSelectAll = (selected: boolean) => {
        let updatedDataItems;
        if (
            groupingType === GroupingType.GroupsWithoutLineItems ||
            groupingType === GroupingType.GroupedLineItems
        ) {
            updatedDataItems = data
                .filter((x) => isLineItemRow(x) || isLineItemGroupRow(x))
                .map((x) => {
                    if (isLineItemRow(x)) {
                        return x;
                    }
                    return {
                        ...x,
                        isSelected: selected && (!canSelectRow || canSelectRow(x)),
                        items: x.items.map((y) => {
                            return {
                                ...y,
                                isSelected: selected && (!canSelectRow || canSelectRow(y)),
                            };
                        }),
                    };
                });
        } else {
            updatedDataItems = dataItems
                .filter((x) => isLineItemRow(x) || isLineItemGroupRow(x))
                .map((x) => {
                    return { ...x, isSelected: selected && (!canSelectRow || canSelectRow(x)) };
                });
        }

        setFieldValue(dataField, updatedDataItems, false);
    };

    const selectedItems = useMemo(() => {
        let selected = dataItems.filter((x) => x.isSelected).map((x) => x.id);
        if (selected.length > 0) {
            // to display the header checkbox as fully checked, we need to include the id of the action row, even though the action row will not have a checkbox
            // if there are no items selected yet, we don't want to have the actionRow as selected under the hood, as the header checkbox will show as partially checked
            const actionRow = dataItems.find((x) => isLineItemActionRow(x));
            if (actionRow) {
                selected.push(actionRow.id);
            }
            const groupedRows = dataItems.filter(isLineItemExpandRow).map((x) => x.id);
            selected = [...selected, ...groupedRows];
        }
        return selected;
    }, [dataItems]);

    const { selectableItemsCount, selectedSelectableItemsCount } = useMemo(() => {
        const selectableItems = dataItems.filter((x) => {
            if (isLineItemRow(x) || isLineItemGroupRow(x)) {
                return !canSelectRow || canSelectRow(x);
            }
            return false;
        });

        const selectedSelectableItems = selectableItems.filter((x) => x.isSelected);

        return {
            selectableItemsCount: selectableItems.length,
            selectedSelectableItemsCount: selectedSelectableItems.length,
        };
    }, [dataItems, canSelectRow]);

    const summarySetup = useCallback(
        (rows: readonly LineItem[]) => {
            let indexOffset = 0;
            if (hasSelectableRows) {
                indexOffset++;
            }
            if (expandable !== undefined) {
                indexOffset++;
            }

            const hasAdditionalRows = !!additionalFooterRows?.length;
            const footerRowClassName = cn("BTGridFooter", {
                "BTGridFooter--compact": hasAdditionalRows,
            });
            return (
                <Table.Summary fixed>
                    <Table.Summary.Row className={footerRowClassName}>
                        {hasSelectableRows && <Table.Summary.Cell key={0} index={0} />}
                        {expandable && (
                            <Table.Summary.Cell key={1} index={hasSelectableRows ? 1 : 0} />
                        )}
                        {columnsList.map((x, index) => {
                            return (
                                <Table.Summary.Cell
                                    key={index + indexOffset}
                                    index={index + indexOffset}
                                    className={cn({
                                        lastColumnWithScroll:
                                            scrollable && index === columnsList.length - 1,
                                    })}
                                >
                                    <BTGridFooterCell
                                        column={x}
                                        value={
                                            x.getFooterValue ? x.getFooterValue(rows) : undefined
                                        }
                                        field={x.jsonKey}
                                        classname={x.ellipsis ? "BTGridFooterCell--ellipsis" : ""}
                                        contentOnly
                                    />
                                </Table.Summary.Cell>
                            );
                        })}
                    </Table.Summary.Row>
                    {additionalFooterRows?.map((row, rowIdx) => (
                        <Table.Summary.Row
                            key={`additional-footer-${rowIdx}`}
                            className="BTGridFooter BTGridFooter--compact"
                            data-testid={row.testid}
                        >
                            {hasSelectableRows && <Table.Summary.Cell key={0} index={0} />}
                            {expandable && (
                                <Table.Summary.Cell key={1} index={hasSelectableRows ? 1 : 0} />
                            )}
                            {columnsList.map((col, colIdx) => {
                                const isValueColumn =
                                    (col.id as LineItemColumns) === row.valueColumnId;
                                return (
                                    <Table.Summary.Cell
                                        key={colIdx + indexOffset}
                                        index={colIdx + indexOffset}
                                        className={cn({
                                            lastColumnWithScroll:
                                                scrollable && colIdx === columnsList.length - 1,
                                        })}
                                    >
                                        {colIdx === 0 ? (
                                            row.label
                                        ) : isValueColumn ? (
                                            <div style={{ textAlign: col.align }}>{row.value}</div>
                                        ) : null}
                                    </Table.Summary.Cell>
                                );
                            })}
                        </Table.Summary.Row>
                    ))}
                </Table.Summary>
            );
        },
        [hasSelectableRows, expandable, columnsList, scrollable, additionalFooterRows],
    );

    const onTableChange = useCallback(
        (
            pagination: TablePaginationConfig,
            _filters: unknown,
            sorter: SorterResult<LineItem> | SorterResult<LineItem>[],
        ) => {
            if (sorter && onSortChange) {
                let sort: SorterResult<LineItem>;
                if (Array.isArray(sorter)) {
                    sort = sorter[0];
                } else {
                    sort = sorter;
                }
                onSortChange([
                    {
                        field: sort.columnKey as string,
                        dir: sort.order === "ascend" ? "asc" : "desc",
                    },
                ]);
            }
        },
        [onSortChange],
    );

    useMemo(
        () =>
            setVT({
                body: {
                    row: LineItemDraggableRow,
                },
            }),
        [setVT],
    );

    const manager = useRef(GlobalDnDContext);
    const selectedLineItems = data.filter((li) => isLineItemRow(li) && li.isSelected);
    const checkedActionChildren = (
        <CheckedActions
            count={selectedLineItems.length}
            onUnselectAll={() => handleSelectAll(false)}
        />
    );
    if (emptyState) {
        // Return the Toolbar and the Empty State
        return (
            <div>
                {renderToolbarContent && renderToolbarContent(checkedActionChildren)}
                {emptyState}
            </div>
        );
    }

    return (
        <div>
            {renderToolbarContent && renderToolbarContent(checkedActionChildren)}
            <BTForm layout="vertical">
                <div
                    className={cn(
                        "LineItemContainer",
                        "BaseLineItemContainer",
                        containerName,
                        additionalClasses,
                    )}
                    ref={onTableRefChange}
                    data-testid="lineItemContainer"
                >
                    <DndProvider manager={manager.current.dragDropManager!}>
                        <HighlightUpdates value={lineItemToJumpTo}>
                            <BTTable
                                key={heightOfTable}
                                pagination={false}
                                dataSource={dataItems}
                                rowKey="id"
                                components={scrollable ? vt : undefined}
                                columns={columnsDisplay}
                                emptyState={false}
                                onRow={onRow}
                                sticky={sticky}
                                style={style}
                                scroll={
                                    scrollable
                                        ? {
                                              x: scrollable,
                                              y: Math.min(
                                                  heightOfTable - 70, // subtracting 70 to account for the header and scrollbars
                                                  size.height + 20,
                                              ),
                                          }
                                        : undefined
                                }
                                expandable={expandable}
                                rowSelection={
                                    hasSelectableRows
                                        ? {
                                              type: "checkbox",
                                              onSelect: handleSelectionChange,
                                              selectedRowKeys: selectedItems,
                                              getCheckboxProps: (record) =>
                                                  ({
                                                      "data-testid": `${dataField}[${data.indexOf(
                                                          record,
                                                      )}].isSelected`,
                                                      disabled: canSelectRow
                                                          ? !canSelectRow(record)
                                                          : false,
                                                  }) as any,
                                              onSelectAll: handleSelectAll,
                                              renderCell: renderSelectionCell,
                                              columnTitle: (
                                                  <BTCheckbox
                                                      id="LineItemSelectAll"
                                                      checked={
                                                          selectableItemsCount > 0 &&
                                                          selectedSelectableItemsCount ===
                                                              selectableItemsCount
                                                      }
                                                      data-testid="LineItemSelectAll"
                                                      indeterminate={
                                                          selectedSelectableItemsCount > 0 &&
                                                          selectedSelectableItemsCount <
                                                              selectableItemsCount
                                                      }
                                                      disabled={selectableItemsCount === 0}
                                                      onChange={(e) => {
                                                          handleSelectAll(e.target.checked);
                                                      }}
                                                      hotkey="checkAll"
                                                  />
                                              ),
                                          }
                                        : undefined
                                }
                                onChange={onTableChange}
                                summary={summarySetup}
                                onHeaderRow={() => ({ className: "BTGridHeader" })}
                            />
                        </HighlightUpdates>
                    </DndProvider>
                </div>
            </BTForm>
        </div>
    );
};
